/**
 * OCR Jobs List Page
 *
 * Scientific dashboard for managing OCR batch imports.
 * Features: Job list, status filtering, upload trigger, delete actions.
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ocrApi, type OcrJob, type OcrJobListResponse } from '@/lib/ocrApi';
import { JobCard, JobCardSkeleton } from '@/components/ocr';
import { Button } from '@/components/ui/Button';
import {
  ScanLine,
  Upload,
  Filter,
  RefreshCw,
  FileX,
  Inbox,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { RoleGuard } from '@/components/layout/RoleGuard';

export const Route = createFileRoute('/_app/ocr/')({
  component: () => (
    <RoleGuard allowedRoles={['owner', 'supervisor', 'operator']}>
      <OcrJobsPage />
    </RoleGuard>
  ),
});

type StatusFilter = 'all' | 'pending' | 'processing' | 'complete' | 'failed';

function OcrJobsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { farmId, isLoading: isFarmLoading } = useCurrentFarm();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Fetch jobs
  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['ocr-jobs', farmId],
    queryFn: () =>
      ocrApi.get<OcrJobListResponse>(`/ocr/jobs`, {
        params: { farm_id: farmId! },
      }),
    refetchInterval: 10000, // Poll every 10s for processing updates
    enabled: !!farmId,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (jobId: string) => ocrApi.delete(`/ocr/jobs/${jobId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ocr-jobs', farmId] });
    },
  });

  const jobs = data?.jobs || [];

  // Filter jobs
  const filteredJobs = statusFilter === 'all'
    ? jobs
    : jobs.filter(job => job.status === statusFilter);

  // Count by status for filter badges
  const statusCounts = jobs.reduce((acc, job) => {
    acc[job.status] = (acc[job.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const handleJobClick = (job: OcrJob) => {
    navigate({ to: '/ocr/$jobId', params: { jobId: job.id } });
  };

  const handleDelete = (jobId: string) => {
    if (confirm('Delete this job and all extracted records?')) {
      deleteMutation.mutate(jobId);
    }
  };

  return (
    <div className="min-h-full">
      {/* Page Header - Industrial panel aesthetic */}
      <div className="border-b border-gray-200 bg-gradient-to-b from-gray-50 to-white">
        <div className="px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-primary-100 rounded-xl">
                <ScanLine className="w-6 h-6 text-primary-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                  OCR Import
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Extract and import data from logbook photos
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                disabled={isRefetching}
                leftIcon={<RefreshCw className={cn('w-4 h-4', isRefetching && 'animate-spin')} />}
              >
                Refresh
              </Button>
              <Button
                variant="primary"
                leftIcon={<Upload className="w-4 h-4" />}
                onClick={() => {
                  // NOTE: Upload functionality - implement image upload modal with batch selection
                  alert('Upload functionality coming soon');
                }}
              >
                Upload Images
              </Button>
            </div>
          </div>

          {/* Status Filter Tabs */}
          <div className="flex items-center gap-2 mt-5">
            <Filter className="w-4 h-4 text-gray-400" />
            <div className="flex gap-1">
              {(['all', 'pending', 'processing', 'complete', 'failed'] as const).map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    statusFilter === status
                      ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  )}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                  {status !== 'all' && statusCounts[status] ? (
                    <span className="ml-1.5 text-xs opacity-60">
                      ({statusCounts[status]})
                    </span>
                  ) : null}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {(isFarmLoading || isLoading) ? (
          // Loading state
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map(i => (
              <JobCardSkeleton key={i} />
            ))}
          </div>
        ) : filteredJobs.length === 0 ? (
          // Empty state
          <div className="flex flex-col items-center justify-center py-16 text-center">
            {jobs.length === 0 ? (
              <>
                <div className="p-4 bg-gray-100 rounded-full mb-4">
                  <Inbox className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  No OCR jobs yet
                </h3>
                <p className="text-gray-500 max-w-sm mb-4">
                  Upload photos of your logbook pages to extract and import data automatically.
                </p>
                <Button
                  variant="primary"
                  leftIcon={<Upload className="w-4 h-4" />}
                  onClick={() => alert('Upload functionality coming soon')}
                >
                  Upload Your First Images
                </Button>
              </>
            ) : (
              <>
                <div className="p-4 bg-gray-100 rounded-full mb-4">
                  <FileX className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  No jobs match this filter
                </h3>
                <p className="text-gray-500">
                  Try selecting a different status filter.
                </p>
              </>
            )}
          </div>
        ) : (
          // Job cards grid
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredJobs.map(job => (
              <JobCard
                key={job.id}
                job={job}
                onClick={() => handleJobClick(job)}
                onDelete={() => handleDelete(job.id)}
              />
            ))}
          </div>
        )}

        {/* Stats footer */}
        {jobs.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>
                Showing {filteredJobs.length} of {jobs.length} jobs
              </span>
              <span className="font-mono">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
